import type { ReactElement, ReactNode } from 'react';
import { Fragment, useState } from 'react';

import type { ISingleHistoryBet, ISingleHistoryGame } from '../../../../../../api/fetchers/history/types';
import { CurrencyConnected } from '../../../../../currency/CurrencyConnected';
import { Icon, Icons } from '../../../../../icons';
import { L12nText } from '../../../../../text/L12nText';
import { TAGameSummary, TAResultCode } from '../../../gameInfo/results/treasureIsland/types';
import historyStyles from '../../HistoryBetInfo.module.css';

import { BLM } from './components/BLM/BLM';
import { BMM } from './components/BMM/BMM';
import { CFT } from './components/CFT/CFT';
import { GemsList } from './components/GemsList/GemsList';
import { JSL } from './components/JSL/JSL';

import styles from './TreasureIslandTableResult.module.css';

interface TIResultsProps {
  bets: ISingleHistoryBet[];
  game: ISingleHistoryGame;
}

const getDescription = (text: string): string => {
  if (text[0] == '{') {
    const parsed = JSON.parse(text);
    return parsed.cardNo || parsed[Object.keys(parsed)[0]];
  }
  return text;
};

export default function TreasureIslandTableResult({ bets, game }: TIResultsProps): ReactElement | null {
  const [openDetails, setOpenDetails] = useState<string | null>(null);
  const taGameSummary = game.additional.taGameSummary as TAGameSummary;
  const gemBets = ['topaz', 'emerald', 'ruby'];
  const gameResult = taGameSummary?.resultDesc?.toLocaleLowerCase() ?? '';
  const hasGemResult = gemBets.includes(gameResult);

  const handleDetailsClick = (detailsName: string): void => {
    setOpenDetails((prev) => {
      if (prev) return null;
      return detailsName;
    });
  };

  return (
    <>
      <thead>
        <tr>
          <td></td>
          <td className={historyStyles.table_head} style={{ textAlign: 'left' }}>
            <L12nText tk="BETTYPE" />
          </td>
          <td className={historyStyles.table_head}>
            <L12nText tk="BET_LABEL" />
          </td>
          <td className={historyStyles.table_head}>
            <L12nText tk="NETCASH" />
          </td>
        </tr>
      </thead>
      <tbody>
        {bets.map((bet: ISingleHistoryBet, index: number) => {
          const betDescription = bet.description.toLocaleLowerCase();
          const isOpened = openDetails === betDescription;
          const hasResult = !hasGemResult && gameResult === betDescription;

          return (
            <Fragment key={index}>
              <tr
                onClick={hasResult ? (): void => handleDetailsClick(betDescription) : void 0}
                className={hasResult ? (isOpened ? styles.tableRow : styles.rowCursor) : ''}
              >
                <td className={styles.leftTopCorner}>
                  {hasResult && (
                    <Icon icon={isOpened ? Icons.ChevronUp : Icons.ChevronDown} className={styles.tableChevron} />
                  )}
                </td>
                <td style={{ textAlign: 'left' }}>{getDescription(bet.description)}</td>
                {bet.playerMul ? <td>{bet.playerMul}</td> : null}
                <td>
                  <CurrencyConnected value={Number(bet.amount)} />
                </td>
                <td
                  className={`${styles.rightTopCorner} ${
                    Number(bet.netCash) > 0 ? historyStyles.win : historyStyles.lose
                  }`}
                >
                  {game.cancelled && Number(bet.netCash) == 0 ? (
                    <span className={historyStyles.refunded}>
                      <L12nText tk="REFUNDED" />
                    </span>
                  ) : (
                    <CurrencyConnected value={Number(bet.netCash)} />
                  )}
                </td>
              </tr>
              {isOpened && (
                <tr className={styles.tableRow}>
                  <td colSpan={4} className={styles.bottomCorners}>
                    <div className={styles.resultCollapse}>
                      {((taGameSummary: TAGameSummary): ReactNode => {
                        switch (Number(taGameSummary.resultCode)) {
                          case TAResultCode.Wild_Collector: {
                            const { entry = [] } = taGameSummary.wcData?.gems || {};
                            const bonusGems = entry.map((data) => ({ key: data.key, val: data.value }));
                            return (
                              <div className={styles.resultsContainer}>
                                <GemsList gemsData={bonusGems} />
                              </div>
                            );
                          }
                          case TAResultCode.Great_Scavenger: {
                            const { gs = [] } = taGameSummary.multipliers || {};
                            return (
                              <div className={styles.resultsContainer}>
                                <GemsList gemsData={gs} />
                              </div>
                            );
                          }
                          case TAResultCode.Bens_Lost_Marbles: {
                            const { totalGems = [], diceValues } = taGameSummary.marblesSummary || {};
                            return (
                              <div className={styles.resultStack}>
                                <BLM diceValues={diceValues || []} />
                                <div className={styles.resultsContainer}>
                                  <GemsList gemsData={totalGems} />
                                </div>
                              </div>
                            );
                          }
                          case TAResultCode.John_Silvers_Loot: {
                            const { bingo = [], bingoWeights = [] } = taGameSummary.bingoSummary || {};

                            return (
                              <div className={styles.resultStack}>
                                <JSL bingoData={bingo} />
                                <div className={styles.resultsContainer}>
                                  <GemsList gemsData={bingoWeights} />
                                </div>
                              </div>
                            );
                          }
                          case TAResultCode.Captains_Flints_Treasure: {
                            const { gemsCount = [], barrelsWon = [] } = taGameSummary.captainFlintSummary || {};
                            return (
                              <div className={styles.resultStack}>
                                <CFT barrels={barrelsWon} />
                                <div className={styles.resultsContainer}>
                                  <GemsList gemsData={gemsCount} />
                                </div>
                              </div>
                            );
                          }
                          case TAResultCode.Billy_Bones_Map: {
                            const {
                              gemsCount = [],
                              playerDecisions = [],
                              chestFound = '',
                              chestPrize = '',
                              chestType = '',
                            } = taGameSummary.mapSummary || {};
                            const chestDetails = { chestFound, chestPrize, chestType };

                            return (
                              <div className={styles.resultStack}>
                                <BMM barrels={playerDecisions} chestDetails={chestDetails} />
                                <div className={styles.resultsContainer}>
                                  <GemsList gemsData={gemsCount} />
                                </div>
                              </div>
                            );
                          }
                          default:
                            return null;
                        }
                      })(taGameSummary)}
                    </div>
                  </td>
                </tr>
              )}
            </Fragment>
          );
        })}
      </tbody>
    </>
  );
}
